/**
 * drift.ts — concept-drift detector (EWMA-ECDD on prediction error).
 *
 * Ported from `EWMADriftDetector` in `_proto_reflex_v1.py` (lines 358-412).
 *
 * Tracks the absolute prediction error |r_observed - r_predicted| of the
 * chosen action. Under stationarity, the error's EWMA settles to a stable
 * mean with bounded variance. When the world drifts, the model starts
 * mispredicting → EWMA error rises above the control limit → DRIFT.
 *
 * Two thresholds (z-scores of the current error against the running
 * baseline):
 *   WARNING when z > L_warning (default 2.0  → ~95th pct of N(0,1))
 *   DRIFT   when z > L_drift   (default 3.0  → ~99.7th pct)
 *
 * At DRIFT we open a re-explore window of K forced-random actions so the
 * bandit collects fresh post-drift data before trusting its model again.
 *
 * Why this complements ensemble disagreement (the v2 H3 fix):
 *   disagreement     = the model's OWN confidence about its predictions.
 *   prediction error = whether the world still MATCHES the model.
 * When token semantics are stable (GOLD=good across all days) the ensemble
 * stays confident (low disagreement) but the SHAPE pattern has shifted, so
 * observed rewards diverge from predictions → drift fires. Exactly the case
 * disagreement missed.
 *
 * Method: Ross, Adams, Taft, Hand (2013), the continuous-signal variant of
 * Gama's DDM (2004).
 */

import type { DriftStatus } from './constants';

export interface DriftHyperparams {
  lam: number; // EWMA decay (small = slow, sticky)
  lWarning: number;
  lDrift: number;
  warmup: number; // ignore the first `warmup` obs (model still stabilising)
  exploreWindow: number; // forced-random action count after a drift
}

export const DEFAULT_DRIFT_HP: DriftHyperparams = {
  lam: 0.05,
  lWarning: 2.0,
  lDrift: 3.0,
  warmup: 50,
  exploreWindow: 24,
};

export class EWMADriftDetector {
  hp: DriftHyperparams;
  private z0 = 0; // baseline EWMA of |error|
  private s0 = 0; // baseline EWMA of error² (for variance)
  private n = 0;
  exploreRemaining = 0;
  nDrift = 0;
  nWarning = 0;

  constructor(hp: Partial<DriftHyperparams> = {}) {
    this.hp = { ...DEFAULT_DRIFT_HP, ...hp };
  }

  /** Full reset for a new seed run. */
  resetRun(): void {
    this.z0 = 0;
    this.s0 = 0;
    this.n = 0;
    this.exploreRemaining = 0;
    this.nDrift = 0;
    this.nWarning = 0;
  }

  /** Feed one (predicted, actual) reward pair. Returns the status. */
  update(pred: number, actual: number): DriftStatus {
    const err = Math.abs(actual - pred);
    this.n++;
    if (this.n <= this.hp.warmup) {
      // During warmup, accumulate the baseline; never fire.
      this.z0 = (1 - this.hp.lam) * this.z0 + this.hp.lam * err;
      this.s0 = (1 - this.hp.lam) * this.s0 + this.hp.lam * err * err;
      return 'warmup';
    }
    this.z0 = (1 - this.hp.lam) * this.z0 + this.hp.lam * err;
    this.s0 = (1 - this.hp.lam) * this.s0 + this.hp.lam * err * err;
    const variance = Math.max(1e-9, this.s0 - this.z0 * this.z0);
    const std = Math.sqrt(variance);
    const z = std > 1e-9 ? (err - this.z0) / std : 0;
    if (z > this.hp.lDrift) {
      this.nDrift++;
      this.exploreRemaining = this.hp.exploreWindow;
      return 'drift';
    }
    if (z > this.hp.lWarning) {
      this.nWarning++;
      return 'warning';
    }
    return 'ok';
  }

  /** True if we're in a forced re-explore window after a drift. Consumes one. */
  shouldExplore(): boolean {
    if (this.exploreRemaining > 0) {
      this.exploreRemaining--;
      return true;
    }
    return false;
  }

  /** Current status for HUD display (non-consuming — for the renderer). */
  status(): DriftStatus {
    if (this.exploreRemaining > 0) return 'drift';
    if (this.n <= this.hp.warmup) return 'warmup';
    return 'ok';
  }
}
